import type { EventStatus, Permission } from '@barso/shared';
import { AREA_GROUPS, areaFeatureOn, visibleAreas, type Area, type AreaFeatures } from './areas';
import type { IconName } from './icons';

/**
 * Untere Leiste und „⋯ Mehr“ der App nach Rolle (Plan 16, U13, Abschnitt 4.4). Reine Funktion ohne Seiteneffekte:
 * Rollen, Basar-Status und gemerkte Kasse hinein, Reiter und Blatt-Einträge heraus. Die Reihenfolge ist fest (kein
 * Sortieren durch Nutzer). Der Server prüft Rechte weiter selbst, hier wird nur angezeigt, was die Person darf.
 */

export type NavTabKey = 'start' | 'kasse' | 'anmeldungen' | 'einsaetze' | 'nachrichten' | 'mehr';

export interface NavTab {
  key: NavTabKey;
  label: string;
  /** Ziel; `mehr` hat keins, es öffnet das Blatt. */
  to: string | null;
  icon: IconName;
}

export interface MoreEntry {
  key: string;
  to: string;
  label: string;
  icon: IconName;
  /** Wozu der Eintrag da ist (kurz, unter dem Namen). */
  description?: string;
  /** Gruppenname im Basar-Abschnitt (Vorbereitung, Basartag, …); fehlt bei der Übersicht. */
  group?: string;
}

export interface MoreSection {
  key: 'basar' | 'weitere';
  /** Überschrift (Name des Basars bzw. „Weiteres“). */
  title: string;
  entries: MoreEntry[];
}

export interface NavEvent {
  eventId: string;
  title: string;
  status: EventStatus;
  roles: readonly string[];
}

export interface NavInput {
  /** Gerät mit Schnellzugang (ohne Konto): keine Leiste. */
  deviceSession: boolean;
  /** Basare mit Rolle der Person (`/me/helper-events`); `null` solange nicht geladen. */
  events: readonly NavEvent[] | null;
  /** Anzahl eigener Anmeldungen als Verkäufer (`/me/registrations`); `null` solange nicht geladen. */
  registrationCount: number | null;
  /**
   * Anzahl eigener Organisationen mit Rolle Inhaber oder Verwaltung (`/orgs`); `null` solange nicht geladen. Reine
   * Mitglieder zählen nicht: Eine Einladung als Kasse legt die Person als Mitglied an.
   */
  managedOrgCount: number | null;
  /** Geöffneter Basar mit den Rechten der Person (aus der Basar-Hülle), sonst `null`. */
  context: {
    eventId: string;
    eventTitle: string | null;
    status?: EventStatus;
    permissions?: readonly Permission[];
    /** Schalter der Zusatzfunktionen des Basars (F0). */
    features?: AreaFeatures | undefined;
  } | null;
  /** Ziel der Kasse (gemerkte oder geöffnete Kasse), `null` ohne. */
  cashTarget: string | null;
}

export type NavVariant = 'device' | 'cashier' | 'multi' | 'plain';

export interface NavModel {
  variant: NavVariant;
  tabs: NavTab[];
  more: MoreSection[];
}

const TAB = {
  start: { key: 'start', label: 'Start', to: '/', icon: 'home' },
  anmeldungen: { key: 'anmeldungen', label: 'Anmeldungen', to: '/meine-anmeldungen', icon: 'tag' },
  einsaetze: { key: 'einsaetze', label: 'Einsätze', to: '/einsaetze', icon: 'calendar' },
  nachrichten: { key: 'nachrichten', label: 'Nachrichten', to: '/nachrichten', icon: 'mail' },
  mehr: { key: 'mehr', label: 'Mehr', to: null, icon: 'more' },
} as const satisfies Record<string, NavTab>;

/** Rollen im Basar, die über Helfen und Kassieren hinausgehen (Annahme, Manager): „mehrere Rollen“. */
const BEYOND_CASH = (role: string): boolean => role !== 'cashier' && role !== 'helper';

const MORE = {
  einsaetze: { key: 'einsaetze', to: '/einsaetze', label: 'Meine Einsätze', icon: 'calendar' },
  nachrichten: { key: 'nachrichten', to: '/nachrichten', label: 'Nachrichten', icon: 'mail' },
  anmeldungen: {
    key: 'anmeldungen',
    to: '/meine-anmeldungen',
    label: 'Meine Anmeldungen',
    icon: 'tag',
  },
  profil: { key: 'profil', to: '/profil', label: 'Profil', icon: 'user' },
  einstellungen: {
    key: 'einstellungen',
    to: '/einstellungen',
    label: 'Einstellungen',
    icon: 'settings',
  },
} as const satisfies Record<string, MoreEntry>;

/**
 * Eintrag „Hilfe“ im Blatt: führt in die Hilfe, vorgefiltert auf die Rolle der Person (nur eine Vorauswahl, die Seite
 * zeigt alle Themen). Vorrang: Veranstalter (Manager oder Verwaltung), Kasse, Helfer und Annahme, Verkäufer. Rollen
 * unbekannt (lädt noch) oder keine: unvorgefilterte Übersicht.
 */
function helpEntry(input: NavInput): MoreEntry {
  const roles = input.events?.flatMap((event) => event.roles) ?? [];
  const role =
    roles.includes('manager') || (input.managedOrgCount ?? 0) > 0
      ? 'veranstalter'
      : roles.includes('cashier')
        ? 'kasse'
        : roles.includes('helper') || roles.includes('intake')
          ? 'helfer'
          : (input.registrationCount ?? 0) > 0
            ? 'verkaeufer'
            : null;
  return {
    key: 'hilfe',
    to: role === null ? '/hilfe' : `/hilfe?rolle=${role}`,
    label: 'Hilfe',
    icon: 'info',
  };
}

/** Zeigt das Blatt den Bereich? Recht und, bei Bereichen einer Zusatzfunktion (F0), der Schalter des Basars. */
export function entryAllowed(
  area: Area,
  permissions: readonly Permission[],
  features: AreaFeatures | undefined,
): boolean {
  const permitted = area.permission === undefined || area.permission([...permissions]);
  return permitted && areaFeatureOn(area, features);
}

/**
 * Basar-Abschnitt des Blatts: Übersicht, dann alle Bereiche, die die Person sieht (Recht, Zusatzschalter, Status), nach
 * den Gruppen des Basar-Menüs geordnet. Eine Liste für Handy und Desktop, damit beide dieselben Bereiche zeigen.
 */
function basarSection(input: NavInput['context']): MoreSection | null {
  if (input?.status === undefined || input.permissions === undefined) return null;
  const { eventId, status, permissions } = input;
  const base = `/basare/${eventId}`;
  const entries: MoreEntry[] = [{ key: 'start', to: base, label: 'Übersicht', icon: 'home' }];
  const areas = visibleAreas([...permissions], input.features, status).filter(
    (area) => area.key !== 'start',
  );
  for (const group of AREA_GROUPS) {
    for (const area of areas) {
      if (area.group !== group.key) continue;
      entries.push({
        key: area.key,
        to: `${base}${area.path}`,
        label: area.title,
        icon: area.icon,
        description: area.description,
        group: group.title,
      });
    }
  }
  return { key: 'basar', title: input.eventTitle ?? 'Aktueller Basar', entries };
}

/**
 * Reiter (höchstens fünf) und Blatt-Einträge je Rolle:
 *
 * - Gerät mit Schnellzugang: keine Leiste, im Blatt nur Einstellungen (Zweig bisher ungenutzt: Geräte ohne Konto laufen
 *   im Kassen-Modus ohne Layout-Leiste; für Tests und spätere Nutzung vorgehalten).
 * - Nur Kassierer (Kasse, ggf. Helfer; keine Anmeldung als Verkäufer, keine Organisation, keine weitere Rolle):
 *   nur „Kasse“ und „⋯ Mehr“.
 * - Kassenrecht und weitere Rollen: Start, Kasse, Nachrichten, ⋯ Mehr; das Blatt zeigt oben die Bereiche des
 *   aktuellen Basars je Status.
 * - Ohne Kassenrecht: Start, Anmeldungen, Einsätze, Nachrichten, ⋯ Mehr.
 *
 * Solange Rollen nicht geladen sind (`null`), gilt für die Kasse der sichere Fall „mehrere Rollen“: Der Reiter
 * „Kasse“ verschwindet nie wegen einer Ladezeit.
 */
export function navModel(input: NavInput): NavModel {
  if (input.deviceSession) {
    return {
      variant: 'device',
      tabs: [],
      more: [{ key: 'weitere', title: 'Weiteres', entries: [MORE.einstellungen] }],
    };
  }
  const kasseTab = (to: string | null): NavTab => ({
    key: 'kasse',
    label: 'Kasse',
    to: to ?? '/kasse',
    icon: 'euro',
  });
  const hasCashRole =
    input.events?.some(
      (event) => event.roles.includes('cashier') || event.roles.includes('manager'),
    ) === true;
  // Rollen unbekannt (lädt oder Fehler): „Kasse“ bleibt sichtbar, der sichere Fall
  const hasCash = input.cashTarget !== null || hasCashRole || input.events === null;
  const onlyCashier =
    hasCash &&
    input.events !== null &&
    input.registrationCount !== null &&
    input.managedOrgCount !== null &&
    input.registrationCount === 0 &&
    input.managedOrgCount === 0 &&
    input.events.every((event) => !event.roles.some(BEYOND_CASH));

  if (onlyCashier) {
    return {
      variant: 'cashier',
      tabs: [kasseTab(input.cashTarget), TAB.mehr],
      more: [
        {
          key: 'weitere',
          title: 'Weiteres',
          entries: [MORE.einsaetze, MORE.nachrichten, MORE.einstellungen, helpEntry(input)],
        },
      ],
    };
  }
  if (hasCash) {
    const basar = basarSection(input.context);
    return {
      variant: 'multi',
      tabs: [TAB.start, kasseTab(input.cashTarget), TAB.nachrichten, TAB.mehr],
      more: [
        ...(basar === null ? [] : [basar]),
        {
          key: 'weitere',
          title: 'Weiteres',
          entries: [
            MORE.anmeldungen,
            MORE.einsaetze,
            MORE.profil,
            MORE.einstellungen,
            helpEntry(input),
          ],
        },
      ],
    };
  }
  const basar = basarSection(input.context);
  return {
    variant: 'plain',
    tabs: [TAB.start, TAB.anmeldungen, TAB.einsaetze, TAB.nachrichten, TAB.mehr],
    more: [
      ...(basar === null ? [] : [basar]),
      {
        key: 'weitere',
        title: 'Weiteres',
        entries: [MORE.profil, MORE.einstellungen, helpEntry(input)],
      },
    ],
  };
}
